import { Controller } from "@hotwired/stimulus"
import { Editor } from "@tiptap/core"
import StarterKit from "@tiptap/starter-kit"
import Link from "@tiptap/extension-link"
import Image from "@tiptap/extension-image"
import Placeholder from "@tiptap/extension-placeholder"

// TipTap-backed rich text editor. Mounts on a div, mirrors editor HTML into
// a hidden textarea so existing form submission and the intern_tasks
// autosave-field pipeline keep working unchanged.
//
// Image paste/drop POSTs the file to upload-url-value, expects {url} back,
// inserts an <img>. URLs typed or pasted as plain text auto-link via the
// Link extension's autolink option.
//
// Public action setContent(html) lets other controllers (e.g. caption-writer)
// replace the editor body programmatically.
//
// Named "tiptap-editor" rather than "rich-text-editor" to avoid colliding
// with the legacy contenteditable-based rich-text-editor controller still
// wired up on admin/clients/show.html.erb.
export default class extends Controller {
  static targets = ["editor", "hiddenInput"]
  static values = {
    uploadUrl: { type: String, default: "" },
    placeholder: { type: String, default: "" }
  }

  connect() {
    this.editor = new Editor({
      element: this.editorTarget,
      extensions: [
        StarterKit,
        Link.configure({
          openOnClick: false,
          autolink: true,
          HTMLAttributes: { target: "_blank", rel: "noopener noreferrer" }
        }),
        Image.configure({ allowBase64: false, inline: false }),
        Placeholder.configure({ placeholder: this.placeholderValue })
      ],
      content: this.hiddenInputTarget.value || "",
      onUpdate: ({ editor }) => this.syncToInput(editor.getHTML()),
      editorProps: {
        attributes: {
          class: "tiptap-content focus:outline-none",
          "data-placeholder": this.placeholderValue
        },
        handlePaste: (view, event) => this.handlePaste(event),
        handleDrop: (view, event) => this.handleDrop(event)
      }
    })
  }

  disconnect() {
    this.editor?.destroy()
    this.editor = null
  }

  syncToInput(html) {
    this.hiddenInputTarget.value = html
    // Existing autosave-field controller listens for input events on its
    // target textarea, so dispatching one keeps autosave working.
    this.hiddenInputTarget.dispatchEvent(new Event("input", { bubbles: true }))
    // For panel-form's dirty-tracking on the side panel.
    this.dispatch("dirty", { bubbles: true })
  }

  setContent(html) {
    if (!this.editor) return
    this.editor.commands.setContent(html)
    this.syncToInput(this.editor.getHTML())
  }

  getText() {
    return this.editor?.getText() || ""
  }

  handlePaste(event) {
    const files = Array.from(event.clipboardData?.files || [])
      .filter(f => f.type.startsWith("image/"))
    if (files.length === 0) return false
    event.preventDefault()
    files.forEach(f => this.uploadAndInsert(f))
    return true
  }

  handleDrop(event) {
    const files = Array.from(event.dataTransfer?.files || [])
      .filter(f => f.type.startsWith("image/"))
    if (files.length === 0) return false
    event.preventDefault()
    files.forEach(f => this.uploadAndInsert(f))
    return true
  }

  async uploadAndInsert(file) {
    if (!this.uploadUrlValue) {
      console.warn("tiptap-editor: image paste with no upload-url-value configured")
      return
    }
    const formData = new FormData()
    formData.append("file", file)
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const res = await fetch(this.uploadUrlValue, {
        method: "POST",
        body: formData,
        headers: { "X-CSRF-Token": csrf, "Accept": "application/json" }
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body.error || `Upload failed: ${res.status}`)
      }
      const { url } = await res.json()
      this.editor.chain().focus().setImage({ src: url }).run()
    } catch (err) {
      console.error("tiptap-editor image upload failed:", err)
      alert(`Could not upload image: ${err.message}`)
    }
  }
}
